import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

/**
 * SERP theme context — persists user preference in localStorage and mirrors
 * it as a `data-theme` attribute on the <html> element so CSS variables
 * flip instantly across every page (no per-component overrides needed).
 */

const STORAGE_KEY = "serp:theme";
const ThemeCtx = createContext({ theme: "light", setTheme: () => {}, toggle: () => {} });

function readStored() {
  if (typeof window === "undefined") return "light";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "light" || stored === "dark") return stored;
  // fall back to system preference on first visit
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => readStored());

  const apply = useCallback((next) => {
    const root = document.documentElement;
    root.setAttribute("data-theme", next);
    root.style.colorScheme = next;
    window.localStorage.setItem(STORAGE_KEY, next);
  }, []);

  useEffect(() => { apply(theme); }, [theme, apply]);

  const setTheme = useCallback((next) => setThemeState(next), []);
  const toggle = useCallback(() => setThemeState((t) => (t === "dark" ? "light" : "dark")), []);

  return (
    <ThemeCtx.Provider value={{ theme, setTheme, toggle }}>
      {children}
    </ThemeCtx.Provider>
  );
}

export function useTheme() { return useContext(ThemeCtx); }
